import type { ReactNode } from 'react';

import { useLocale } from '../../preferences/i18n/LocaleProvider';
import type { GraphNodeTypeVisualConfig } from '../services/graph-appearance-config';

export function GraphAppearancePanel({
	controls,
	expanded,
	onExpandedChange,
	onToggleTypeLabels,
	onTypeColorChange,
	onTypeBrightnessChange,
	className = '',
	panelId = 'graph-appearance-panel-content',
	children,
}: {
	controls: GraphNodeTypeVisualConfig[];
	expanded: boolean;
	onExpandedChange: (expanded: boolean) => void;
	onToggleTypeLabels: (typeId: string) => void;
	onTypeColorChange: (typeId: string, colorHex: string) => void;
	onTypeBrightnessChange: (typeId: string, brightness: number) => void;
	className?: string;
	panelId?: string;
	children?: ReactNode;
}) {
	const { t } = useLocale();

	return (
		<section className={className ? `graph-appearance-panel ${className}` : 'graph-appearance-panel'} data-testid="graph-appearance-panel">
			<button
				aria-controls={panelId}
				aria-expanded={expanded}
				className="graph-appearance-panel__toggle"
				onClick={() => onExpandedChange(!expanded)}
				type="button"
			>
				<span className="mode-switcher__eyebrow">{t('workspace.appearance.eyebrow')}</span>
				<strong>{t('workspace.appearance.title')}</strong>
				<span className="workspace-controls-toggle__hint">
					{expanded ? t('workspace.mode.controlsHide') : t('workspace.mode.controlsShow')}
				</span>
			</button>
			{expanded ? (
				<div className="graph-appearance-panel__content" id={panelId}>
					<p className="hud-hint graph-appearance-panel__hint">{t('workspace.appearance.hint')}</p>
					<div aria-label={t('workspace.appearance.typeVisibility')} className="graph-appearance-panel__type-toggles" role="group">
						{controls.map((control) => (
							<button
								aria-pressed={control.labelsVisible}
								className="hud-chip graph-scene-3d__tool graph-appearance-panel__type-toggle"
								key={control.typeId}
								onClick={() => onToggleTypeLabels(control.typeId)}
								style={{ borderColor: `color-mix(in srgb, ${control.colorHex} 44%, rgba(170, 214, 255, .18))` }}
								type="button"
							>
								<span className="graph-appearance-panel__type-dot" style={{ backgroundColor: control.colorHex, opacity: Math.min(1, Math.max(0.4, control.brightness / 1.3)) }} />
								{control.label}
							</button>
						))}
					</div>
					<div className="graph-appearance-panel__type-appearance-list">
						{controls.map((control) => (
							<section className="graph-appearance-panel__type-appearance-card" key={`${control.typeId}-appearance`}>
								<div className="graph-appearance-panel__type-appearance-header">
									<strong>{control.label}</strong>
									<span className="graph-scene-3d__meta-chip">
										{formatMessage(t('workspace.appearance.brightnessValue'), { value: String(control.brightness.toFixed(2)) })}
									</span>
								</div>
								<label className="graph-appearance-panel__type-field">
									<span className="hud-hint">{t('workspace.appearance.color')}</span>
									<input
										aria-label={formatMessage(t('workspace.appearance.colorForType'), { name: control.label })}
										className="graph-appearance-panel__color-input"
										onChange={(event) => onTypeColorChange(control.typeId, event.target.value)}
										type="color"
										value={control.colorHex}
									/>
								</label>
								<label className="graph-appearance-panel__type-field">
									<span className="hud-hint">{t('workspace.appearance.brightness')}</span>
									<input
										aria-label={formatMessage(t('workspace.appearance.brightnessForType'), { name: control.label })}
										className="graph-appearance-panel__brightness-input"
										max="1.3"
										min="0.45"
										onChange={(event) => onTypeBrightnessChange(control.typeId, Number(event.target.value))}
										step="0.05"
										type="range"
										value={control.brightness}
									/>
								</label>
							</section>
						))}
					</div>
					{children}
				</div>
			) : null}
		</section>
	);
}

function formatMessage(template: string, values: Record<string, string>): string {
	return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}